import { useEffect, useMemo, useState } from "react";
import Button from "../atoms/button";
import { getModelsCatalog, serializarTemperatura } from "./functions";

function shortLabel(id, label) {
    if (label) return label;
    if (!id) return "?";
    const s = String(id).replace(/^openrouter:/, "");
    const parts = s.split("/");
    return parts[parts.length - 1] || s;
}

function catalogModels(catalog) {
    return [
        ...(catalog?.recommended || []),
        ...(catalog?.costBenefit || []),
        ...(catalog?.all || []),
    ];
}

function catalogSupportsTemperature(catalog, modelId) {
    const match = catalogModels(catalog).find((m) => m.id === modelId);
    return match?.supportsTemperature === true;
}

export const HAIKU_MODEL_ID = "anthropic/claude-haiku-4.5";

export function buildComparePreselection(data) {
    const haiku = HAIKU_MODEL_ID;
    const cb = (data?.costBenefit || []).map((m) => m.id);
    const pre = [];
    if (cb.includes(haiku) || (data?.recommended || []).some((m) => m.id === haiku)) {
        pre.push(haiku);
    }
    for (const id of cb) {
        if (pre.length >= 3) break;
        if (!pre.includes(id)) pre.push(id);
    }
    if (pre.length < 2) {
        for (const m of data?.recommended || []) {
            if (pre.length >= 3) break;
            if (!pre.includes(m.id)) pre.push(m.id);
        }
    }
    return pre.slice(0, 3);
}

export default function CompareSetup({
    open,
    onClose,
    onConfirm,
    token,
    caseDescription,
    confirming = false,
}) {
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [temps, setTemps] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [filter, setFilter] = useState("");
    const [manualModel, setManualModel] = useState("");

    useEffect(() => {
        if (!open || !token) return undefined;
        let cancelled = false;
        setLoading(true);
        getModelsCatalog(token).then((data) => {
            if (cancelled) return;
            if (data?.type === "error") {
                setCatalog(null);
            } else {
                setCatalog(data);
                setSelected(buildComparePreselection(data));
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [open, token]);

    useEffect(() => {
        if (!open) {
            setSelected([]);
            setTemps({});
            setModalOpen(false);
            setFilter("");
            setManualModel("");
        }
    }, [open]);

    const recommended = catalog?.recommended ?? [];
    const costBenefit = catalog?.costBenefit ?? [];

    const filteredModels = useMemo(() => {
        const allModels = catalog?.all ?? [];
        const q = filter.trim().toLowerCase();
        if (!q) return allModels;
        return allModels.filter(
            (m) =>
                m.id.toLowerCase().includes(q) ||
                (m.label && m.label.toLowerCase().includes(q)) ||
                (m.provider && m.provider.toLowerCase().includes(q))
        );
    }, [catalog, filter]);

    const toggleModel = (id) => {
        setSelected((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= 3) return prev;
            return [...prev, id];
        });
    };

    const canConfirm = selected.length >= 2 && selected.length <= 3 && !confirming;

    const handleConfirm = () => {
        if (!canConfirm) return;
        const models = selected.map((id) => {
            const entry = { id };
            if (catalogSupportsTemperature(catalog, id)) {
                entry.temperatura = serializarTemperatura(temps[id]);
            }
            return entry;
        });
        onConfirm?.({ models });
    };

    if (!open) return null;

    const chipClass = (active, disabled) =>
        `rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
            disabled
                ? "border-gray-700 text-gray-600 cursor-not-allowed"
                : active
                  ? "border-[#d59106] bg-[#d59106] text-black cursor-pointer"
                  : "border-gray-600/60 bg-gray-900/60 text-gray-300 hover:border-[#b47c06]/60 cursor-pointer"
        }`;

    return (
        <div
            className="rounded-lg border border-[#b47c06]/50 bg-gray-900/80 p-4 space-y-3"
            data-testid="compare-setup"
            role="region"
            aria-label="Comparar modelos"
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <h4 className="text-sm font-medium text-laranjinha">Comparar modelos</h4>
                    {caseDescription ? (
                        <p className="text-xs text-gray-400 mt-0.5">Cenário: {caseDescription}</p>
                    ) : null}
                    <p className="text-xs text-gray-500 mt-1">
                        Até 3 modelos em paralelo, colunas lado a lado. Não use “Executar cenário”
                        (isso roda um único modelo).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-white text-sm shrink-0"
                    data-testid="compare-setup-close"
                >
                    Fechar
                </button>
            </div>

            {loading ? (
                <p className="text-sm text-gray-400">Carregando catálogo…</p>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Modelos">
                        {recommended.map((m) => {
                            const active = selected.includes(m.id);
                            const disabled = !active && selected.length >= 3;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    className={chipClass(active, disabled)}
                                    disabled={disabled}
                                    onClick={() => toggleModel(m.id)}
                                    data-testid={`compare-model-chip-${m.id}`}
                                >
                                    {m.label || shortLabel(m.id)}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            className={chipClass(false, selected.length >= 3)}
                            disabled={selected.length >= 3}
                            onClick={() => setModalOpen(true)}
                            data-testid="compare-model-other"
                        >
                            Outro modelo…
                        </button>
                    </div>

                    {costBenefit.length > 0 ? (
                        <div data-testid="compare-cost-benefit-chips">
                            <p className="text-xs text-gray-500 mb-1">Custo-benefício</p>
                            <div className="flex flex-wrap gap-2" role="group" aria-label="Custo-benefício">
                                {costBenefit.map((m) => {
                                    const active = selected.includes(m.id);
                                    const disabled = !active && selected.length >= 3;
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            className={chipClass(active, disabled)}
                                            disabled={disabled}
                                            onClick={() => toggleModel(m.id)}
                                            data-testid={`compare-cost-benefit-chip-${m.id}`}
                                        >
                                            {m.label || shortLabel(m.id)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {selected.length > 0 ? (
                        <div className="space-y-2">
                            {selected.map((id) => {
                                const meta = catalogModels(catalog).find((m) => m.id === id);
                                const label = meta?.label || shortLabel(id);
                                const showTemp = catalogSupportsTemperature(catalog, id);
                                return (
                                    <div
                                        key={id}
                                        className="flex flex-wrap items-center gap-2 text-xs"
                                        data-testid={`compare-selected-${id}`}
                                    >
                                        <span className="text-gray-300 font-mono">{label}</span>
                                        <button
                                            type="button"
                                            onClick={() => toggleModel(id)}
                                            className="text-red-400 hover:text-red-300"
                                            aria-label={`Remover ${label}`}
                                        >
                                            ×
                                        </button>
                                        {showTemp ? (
                                            <label className="flex items-center gap-1">
                                                <span className="text-gray-500">temp</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={1}
                                                    step={0.1}
                                                    value={temps[id] ?? ""}
                                                    onChange={(e) =>
                                                        setTemps((prev) => ({
                                                            ...prev,
                                                            [id]: e.target.value,
                                                        }))
                                                    }
                                                    className="w-16 rounded border border-gray-600 bg-white p-1 text-black"
                                                    data-testid={`compare-temp-${id}`}
                                                    placeholder="0.2"
                                                />
                                            </label>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    <p className="text-[10px] text-gray-500">
                        {selected.length}/3 selecionados (mínimo 2)
                    </p>
                </>
            )}

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                    className="px-4 py-2 font-medium text-black bg-[#d59106] hover:bg-[#b47c06] disabled:opacity-50 disabled:cursor-not-allowed rounded"
                    data-testid="compare-confirm"
                >
                    {confirming
                        ? "Iniciando…"
                        : `Rodar comparação lado a lado (${selected.length})`}
                </button>
                <Button
                    click={onClose}
                    classes="bg-gray-700 hover:bg-gray-600 px-3 py-2 text-white"
                >
                    Cancelar
                </Button>
            </div>

            {modalOpen ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    role="dialog"
                    aria-modal="true"
                    data-testid="compare-model-modal"
                >
                    <div className="w-full max-w-lg rounded-xl border border-[#d59106]/50 bg-gray-900 p-4 shadow-xl">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-laranjinha">Outro modelo</h3>
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="text-gray-400 hover:text-white text-sm"
                            >
                                Fechar
                            </button>
                        </div>
                        <input
                            type="search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filtrar modelos…"
                            className="w-full mb-3 rounded-lg border border-gray-600/50 bg-white p-2 text-sm text-black"
                        />
                        <ul className="max-h-48 overflow-y-auto mb-3 space-y-1">
                            {filteredModels.map((m) => (
                                <li key={m.id}>
                                    <button
                                        type="button"
                                        className="w-full text-left rounded-md px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-800"
                                        onClick={() => {
                                            toggleModel(m.id);
                                            setModalOpen(false);
                                        }}
                                    >
                                        <span className="font-medium">{m.label}</span>
                                        <span className="text-gray-500 text-xs ml-2 font-mono">
                                            {m.id}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="border-t border-gray-700 pt-3 space-y-2">
                            <p className="text-xs text-gray-400">Ou digite fornecedor/modelo:</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={manualModel}
                                    onChange={(e) => setManualModel(e.target.value)}
                                    placeholder="anthropic/claude-sonnet-5"
                                    className="flex-1 rounded-lg border border-gray-600/50 bg-white p-2 text-sm text-black font-mono"
                                />
                                <Button
                                    click={() => {
                                        const v = manualModel.trim();
                                        if (!v) return;
                                        toggleModel(v);
                                        setModalOpen(false);
                                    }}
                                    disabled={!manualModel.trim()}
                                    classes="bg-[#d59106] hover:bg-[#b47c06] px-3 py-2 rounded-lg text-sm"
                                >
                                    Usar
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
